import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect } from "react";

import { cn } from "~/core/generic/shadcn-utils";
import If from "~/core/ui/If";

import OutlinedButton from "~/components/Buttons/OutlinedButton";

import { faPlus, faSearch } from "@fortawesome/pro-regular-svg-icons";

import { useAddAccount } from "../../hooks/useAddAccount";

interface AddAccountProps {
  inputValue: string;
  isSearchEnabled: boolean;
  setIsOpenDropdown: (isOpen: boolean) => void;
}

export const AddAccount = (props: AddAccountProps) => {
  const { inputValue, isSearchEnabled, setIsOpenDropdown } = props;
  const { handleOnAddAccount, addingNewAccount, isValidDomain } =
    useAddAccount(inputValue);

  const addAccount = useCallback(async () => {
    await handleOnAddAccount();
    setIsOpenDropdown(false);
  }, [handleOnAddAccount, setIsOpenDropdown]);

  // Trigger handleOnAddAccount on enter key press
  useEffect(() => {
    if (!isValidDomain) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        addAccount();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [addAccount, isValidDomain]);

  return (
    <div className="flex flex-col gap-4 py-3">
      <If
        condition={isSearchEnabled}
        fallback={
          <div className="text-center text-xs text-light">
            <FontAwesomeIcon icon={faSearch} />
            <p>Search for account...</p>
          </div>
        }
      >
        <p className="text-center text-xs text-light">
          No account matched your search. Let&apos;s create with a{" "}
          <b className={cn(isValidDomain ? "text-success-base" : "text-light")}>
            valid domain (e.g. octolane.com)
          </b>
        </p>
        <OutlinedButton
          type="dashed"
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            handleOnAddAccount();
          }}
          disabled={!isValidDomain}
          loading={addingNewAccount}
          icon={<FontAwesomeIcon icon={faPlus} />}
        >
          Create and add
        </OutlinedButton>
      </If>
    </div>
  );
};
